import {
  type DELIVERY_PROPOSAL_CONTRACT_STATUS,
  type OFFER_CARGO_PROCESS_PROPOSAL_STATUS,
  type OFFER_CARGO_PROCESS_RECEPTION_STATUS,
  getDeliveryProposalContractStatusLabel,
  getOfferCargoProcessReceptionStatusLabel,
} from "@/features/offer-cargo-process-status/badge-status";
import { assertNever } from "@/shared/lib/assertNever";
import { BaseIcon } from "@/shared/ui/BaseIcons";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckIcon from "@mui/icons-material/Check";
import ErrorIcon from "@mui/icons-material/Error";
import { Stack, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";

const BADGE_BACKGROUND_COLOR_ON_GOING = "#52A1D6";
const BADGE_BACKGROUND_COLOR_WAITING = "#F2AC49";
const BADGE_BACKGROUND_COLOR_CONTRACT_CONCLUDED = "#9973E5";
const BADGE_BACKGROUND_COLOR_ACTION_REQUIRED = "#EA583B";
const BADGE_BACKGROUND_COLOR_CLOSED = grey[600];
const BADGE_BACKGROUND_COLOR_SUSPENDED = grey[400];
const BADGE_BACKGROUND_COLOR_DONE = "#66736F";
const BADGE_BACKGROUND_COLOR_NEW = "#FF6666"; // TODO: figma上グラデーションになっているので後ほど対応
const BADGE_BACKGROUND_COLOR_REJECTED = "#FEE7E2";
const BADGE_LABEL_COLOR_REJECTED = "#EA583B";

export type OfferCargoStatusBadgeProps = {
  onClick?: () => void;
} & (
  | {
      statusType: "offer_cargo_process_reception";
      status: OFFER_CARGO_PROCESS_RECEPTION_STATUS;
    }
  | {
      statusType: "delivery_proposal_contract";
      status: DELIVERY_PROPOSAL_CONTRACT_STATUS;
    }
  | {
      statusType: "offer_cargo_process_proposal";
      status: OFFER_CARGO_PROCESS_PROPOSAL_STATUS;
    }
);

// TODO: OfferCargoStatusBadge にサイズ機能を追加する
export const OfferCargoStatusBadge = (props: OfferCargoStatusBadgeProps) => {
  const clickable = props.onClick !== undefined;

  let label: string;
  const labelColor: string = "white";
  let backgroundColor: string;
  let headingIcon: React.ReactNode = undefined;
  let subLabel: string | undefined = undefined;
  let subLabelColor: string | undefined = undefined;
  let subBackgroundColor: string | undefined = undefined;
  let subHeadingIcon: React.ReactNode = undefined;
  switch (props.statusType) {
    case "offer_cargo_process_reception": {
      label = getOfferCargoProcessReceptionStatusLabel(props.status);
      switch (props.status) {
        case "accepting":
          backgroundColor = BADGE_BACKGROUND_COLOR_ON_GOING;
          break;
        case "suspended":
          backgroundColor = BADGE_BACKGROUND_COLOR_SUSPENDED;
          break;
        case "accepting_with_rejection":
          subLabel = "見送られました";
          backgroundColor = BADGE_BACKGROUND_COLOR_ON_GOING;
          subLabelColor = BADGE_LABEL_COLOR_REJECTED;
          subBackgroundColor = BADGE_BACKGROUND_COLOR_REJECTED;
          subHeadingIcon = (
            <ErrorIcon
              sx={{
                fontSize: "14px",
                color: BADGE_LABEL_COLOR_REJECTED,
              }}
            />
          );
          break;
        case "waiting_response":
          backgroundColor = BADGE_BACKGROUND_COLOR_WAITING;
          break;
        case "closed":
          backgroundColor = BADGE_BACKGROUND_COLOR_CLOSED;
          break;
        case "contract_concluded":
          backgroundColor = BADGE_BACKGROUND_COLOR_CONTRACT_CONCLUDED;
          break;
        default:
          assertNever(props);
          throw new Error(`unkown status: ${props}`);
      }
      break;
    }
    case "delivery_proposal_contract": {
      label = getDeliveryProposalContractStatusLabel(props.status);
      switch (props.status) {
        case "not_responded":
          backgroundColor = BADGE_BACKGROUND_COLOR_ACTION_REQUIRED;
          break;
        case "rejected_by_them":
          backgroundColor = BADGE_BACKGROUND_COLOR_CLOSED;
          break;
        case "rejected_by_us":
          backgroundColor = BADGE_BACKGROUND_COLOR_CLOSED;
          break;
        case "waiting_response":
          backgroundColor = BADGE_BACKGROUND_COLOR_WAITING;
          break;
        case "closed":
          backgroundColor = BADGE_BACKGROUND_COLOR_CLOSED;
          break;
        case "contract_concluded":
          backgroundColor = BADGE_BACKGROUND_COLOR_CONTRACT_CONCLUDED;
          break;
        default:
          assertNever(props);
          throw new Error(`unkown status: ${props}`);
      }
      break;
    }
    case "offer_cargo_process_proposal": {
      switch (props.status) {
        case "new":
          label = "新着";
          backgroundColor = BADGE_BACKGROUND_COLOR_NEW;
          headingIcon = (
            <AutoAwesomeIcon
              sx={{
                fontSize: "14px",
                color: "white",
              }}
            />
          );
          break;
        case "already_proposed":
          label = "提案済";
          backgroundColor = BADGE_BACKGROUND_COLOR_DONE;
          headingIcon = (
            <CheckIcon
              sx={{
                fontSize: "14px",
                color: "white",
              }}
            />
          );
          break;
        default:
          assertNever(props);
          throw new Error(`unkown status: ${props}`);
      }
      break;
    }
    default:
      assertNever(props);
      throw new Error(`unkown status: ${props}`);
  }

  return (
    <Stack alignItems="flex-start" spacing={0.5}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        gap={0.5}
        sx={{
          padding: "8px 6px",
          border: "none",
          height: "100%",
          borderTopLeftRadius: "2px",
          borderBottomLeftRadius: "2px",
          borderTopRightRadius: clickable ? "999px" : "2px",
          borderBottomRightRadius: clickable ? "999px" : "2px",
          backgroundColor,
          color: "white",
          fontSize: "14px",
          "& .MuiChip-filled:hover": {
            backgroundColor: `${backgroundColor}`,
          },
          "& .MuiChip-filled:active": {
            backgroundColor: `${backgroundColor}`,
          },
          "& .MuiChip-label": {
            padding: "0",
            paddingRight: clickable ? "6px" : "0px",
          },
          "& .MuiChip-deleteIcon": {
            margin: 0,
            borderLeft: "1px solid #FFFFFF33",
            padding: "0",
            fontSize: "24px",
            color: labelColor,
            ":hover": {
              color: labelColor,
            },
          },
        }}
        onClick={props.onClick}
      >
        {headingIcon}
        <Typography
          fontSize="14px"
          color={labelColor}
          sx={{
            lineHeight: "14px",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Typography>
        {clickable ? (
          <BaseIcon
            name="chevronRight"
            htmlColor={labelColor}
            sx={{ fontSize: "14px" }}
          />
        ) : null}
      </Stack>
      {subLabel && (
        <Stack
          direction="row"
          alignItems="center"
          gap={0.5}
          p={0.5}
          sx={{
            backgroundColor: subBackgroundColor,
            borderRadius: "2px",
          }}
        >
          {subHeadingIcon}
          <Typography
            fontSize="12px"
            color={subLabelColor}
            sx={{
              lineHeight: "12px",
              whiteSpace: "nowrap",
            }}
          >
            {subLabel}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};
